// useGridFeasibility — fetches grid-feasibility data for a single selected
// project. Wraps two endpoints the analyst reads together on the selected
// card:
//
//   POST /api/v1/grid-feasibility/assess   → structured payload:
//                                            area_context, time_to_power,
//                                            connection_realism, queue_drop,
//                                            reinforcement_dependency,
//                                            capacity_release, time_risk,
//                                            phased_build, key_insights,
//                                            explanation.
//   GET  /api/v1/grid-feasibility/statnett-station
//                                          → the Statnett station nearest the
//                                            site, as Statnett's own lists give
//                                            it: reserved, queued, withdrawn
//                                            and connected customers, with the
//                                            lists' snapshot date and sources.
//
// The card used to read /queue, whose Norwegian rows mixed hand-written seed
// rows ("Yara Herøya Green Ammonia 450 MW") with a March 2026 export, all
// labelled with the request date. The station picture replaced it on
// 2026-10-13.
//
// NOTE: /analyze (the sibling endpoint) only returns AI-generated markdown
// — it does NOT carry the structured fields the block renders. Fix landed
// 2026-09-09 after an audit found every headline number showing "—".
//
// Kept as a component-local hook (not in ProjectsContext) because the data
// is per-selection view state, not per-org state — no other surface reads
// it, and it lives and dies with the card.
//
// Cache key = project.id. Switching selection back to a previously-loaded
// site reuses its cached blob instead of re-hitting the upstream service.

import { useEffect, useRef, useState } from 'react';

const API_BASE = import.meta.env?.VITE_API_URL || '';

// Mirrors ProjectsPage.fetchGridFeasibility's voltage selector — the
// upstream enum expects one of 66kV / 132kV / 300kV / 420kV.
function voltageParam(kv) {
  const n = Number(kv);
  if (!Number.isFinite(n)) return '132kV';
  if (n >= 420) return '420kV';
  if (n >= 300) return '300kV';
  if (n >= 132) return '132kV';
  return '66kV';
}

/**
 * @param {{ id, lat, lon, enrichment?: object }?} project
 * @returns {{ status, feasibility, statnett, error, retry }}
 *   status ∈ { 'idle', 'loading', 'ready', 'error' }
 *   feasibility = /assess response            (or null)
 *   statnett    = /statnett-station response  (or null)
 *   error       = string | null
 *   retry       = function to re-fetch on click
 */
export function useGridFeasibility(project) {
  const [cache] = useState(() => new Map());  // id → { status, feasibility, statnett, error }
  const [tick, setTick] = useState(0);        // force re-render on cache mutation
  const inFlight = useRef(new Set());          // ids currently loading, guards double-fire

  const id = project?.id ?? null;

  useEffect(() => {
    if (!project || project.lat == null || project.lon == null) return;
    if (cache.has(project.id)) return;         // already fetched (or failed)
    if (inFlight.current.has(project.id)) return;
    inFlight.current.add(project.id);
    cache.set(project.id, { status: 'loading', feasibility: null, statnett: null, error: null });
    setTick(t => t + 1);

    const controller = new AbortController();
    // Upstream grid-feasibility is an external AI service; without a
    // deadline a hung call left the card on "Fetching…" indefinitely.
    const deadline = setTimeout(() => controller.abort('timeout'), 30000);
    (async () => {
      const e = project.enrichment || {};
      // Centre the assessment on the CONNECTION substation when we know
      // where it is — the upstream service otherwise picks its own nearest
      // station (a 47 kV distribution point for a 132 kV connection).
      const e0 = project.enrichment || {};
      const body = {
        lat: e0.nearest_grid_lat ?? project.lat,
        lng: e0.nearest_grid_lon ?? project.lon,
        requested_mw: 20,                       // headline MW; the user
                                                // can re-run per-MW in
                                                // the full analysis view
        project_type: 'datacenter',
        connection_voltage: voltageParam(e.nearest_grid_voltage_kv),
        project_window_years: 5,
      };
      // Statnett's lists are per transmission station. Describe the one above
      // the connection point: the station WattApp names above the screen's
      // grid point, else the nearest to that point (or to the connection
      // substation, or to the site). Until 2026-10-13 this was the station
      // nearest the site: Bamble TRA for a site connecting via Roligheten,
      // which WattApp places under Grenland.
      const gp = e.harbour_screen?.grid_point || {};
      const conn = gp.lat != null && gp.lon != null
        ? { lat: gp.lat, lon: gp.lon, via: gp.name || null, station: gp.statnett_parent || null }
        : e.nearest_grid_lat != null && e.nearest_grid_lon != null
          ? { lat: e.nearest_grid_lat, lon: e.nearest_grid_lon, via: e.nearest_grid_name || null, station: null }
          : { lat: project.lat, lon: project.lon, via: null, station: null };
      const stationQs = new URLSearchParams({
        lat: String(conn.lat), lng: String(conn.lon), ...(conn.station ? { station: conn.station } : {}),
      }).toString();

      try {
        const [assessResp, stationResp] = await Promise.all([
          fetch(`${API_BASE}/api/v1/grid-feasibility/assess`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify(body),
          }),
          fetch(`${API_BASE}/api/v1/grid-feasibility/statnett-station?${stationQs}`, {
            signal: controller.signal,
          }),
        ]);
        const feasibility = assessResp.ok  ? await assessResp.json()  : null;
        const statnett    = stationResp.ok ? { ...(await stationResp.json()), via: conn.via } : null;
        if (!feasibility && !statnett) throw new Error('Grid feasibility service unavailable');
        cache.set(project.id, { status: 'ready', feasibility, statnett, error: null });
      } catch (err) {
        if (err.name === 'AbortError' || controller.signal.aborted) {
          // Deadline → show a retryable error. Unmount / StrictMode
          // double-invoke → drop the entry so the next run refetches
          // instead of finding a permanent 'loading' in the cache.
          if (controller.signal.reason === 'timeout') {
            cache.set(project.id, { status: 'error', feasibility: null, statnett: null,
                                    error: 'Grid feasibility service did not answer within 30 s' });
          } else {
            cache.delete(project.id);
          }
          return;
        }
        cache.set(project.id, {
          status: 'error',
          feasibility: null,
          statnett: null,
          error: err.message || 'Grid feasibility fetch failed',
        });
      } finally {
        clearTimeout(deadline);
        inFlight.current.delete(project.id);
        setTick(t => t + 1);
      }
    })();

    // No abort on cleanup (2026-10-05). StrictMode runs effect → cleanup →
    // effect synchronously; aborting here left the second run blocked by
    // the in-flight guard and the entry deleted → the block vanished.
    // The result is keyed by project id and stays useful after an
    // unmount, so let the request finish. The 30 s deadline still applies.
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const entry = id != null ? cache.get(id) : null;
  const status = entry?.status || 'idle';

  const retry = () => {
    if (!project) return;
    cache.delete(project.id);
    inFlight.current.delete(project.id);
    setTick(t => t + 1);
  };

  return {
    status,
    feasibility: entry?.feasibility || null,
    statnett:    entry?.statnett    || null,
    error:       entry?.error       || null,
    retry,
  };
}
